import { ModalManager } from '../../core/utils/modal-manager.js';
import { PromptManager } from '../../core/utils/prompt-manager.js';
import { UserService } from '../../core/services/user-service.js';
import { GroupService } from '../../core/services/group-service.js';
import { icon } from '../../core/icons/icons.js';
import { i18n } from '../../core/i18n/i18n-service.js';
import type { IToastService } from '../../core/services/toast-service.js';
import '../eum-search-box/eum-search-box.js';

/**
 * The bulk-action capabilities the toolbar surfaces. This is the shared subset
 * of the host component's capability table that governs the toolbar buttons and
 * the add-user flows. Manage-scoped actions (add) require a manageable group;
 * read-only bulk actions (email / copy / export) only require admin access.
 */
export interface UserToolbarCapabilities {
    canAddUsers: boolean;
    canEmailUsers: boolean;
    canCopyUsers: boolean;
    canExportUsers: boolean;
}

/**
 * The contract a host component must satisfy to embed {@link EumUserToolbar}.
 *
 * The toolbar is intentionally a controller (not a custom element) so it can
 * operate inside the host's own Shadow DOM — sharing its `ModalManager`,
 * services, and adopted stylesheets — while acting on the host's *current
 * filtered result set*. Both `eum-user-management` (standalone) and
 * `eum-member-directory` (directory host) provide this context.
 */
export interface UserToolbarHost {
    /** The host's shadow root (where the add-user modal is mounted). */
    readonly shadow: ShadowRoot;
    /** The host's modal controller, reused so backdrops stack correctly. */
    readonly modals: ModalManager;
    /** The host's prompt controller for confirm/alert dialogs layered over modals. */
    readonly prompts: PromptManager;
    /** Shared toast service for user notifications. */
    readonly toast: IToastService;
    /** API wrapper for user endpoints (add / invite). */
    readonly userService: UserService;
    /** API wrapper for group endpoints (export). */
    readonly groupService: GroupService;
    /** The bulk-action capabilities gating each toolbar surface. */
    readonly capabilities: UserToolbarCapabilities;
    /** The active group id; required for add / remove membership actions. */
    readonly groupId: string;
    /** The search endpoint used by the "add existing" lookup. */
    readonly searchUrl: string;
    /** Optional direct API base URL (`{base}/eum_api/v1/`) when not using proxy routes. */
    readonly apiBaseUrl?: string;
    /** The Select projection to request for lookups / export. */
    readonly selectFields: string[] | undefined;
    /** The OrderBy projection to request for export. */
    readonly orderByFields: string[] | undefined;

    /** The current OData filter (already group-scoped by the host). */
    getFilter(): string;
    /** The active search text driving the current result set. */
    getSearchText(): string;
    /** The current filtered result set the bulk actions operate on. */
    getCurrentResults(): Array<{ document: Record<string, any> }>;

    /** Resolve a person's display name from a result document. */
    displayName(doc: Record<string, any>): string;
    /** Resolve a person's email from a result document. */
    emailOf(doc: Record<string, any>): string;
    /** Resolve whether a result document has a profile image. */
    hasImageOf(doc: Record<string, any>): boolean;
    /** Resolve the profile image URL for a result document. */
    avatarImageUrlOf(doc: Record<string, any>): string;
    /** Escape a string for safe HTML text interpolation. */
    escapeHTML(str: string): string;
    /** Escape a string for safe HTML attribute interpolation. */
    escapeAttr(str: string): string;

    /** Re-run the host search so results reflect membership changes. */
    refreshResults(): void;
}

/** The add-user sub-flows, matching the legacy toolbar options. */
type AddMode = 'existing' | 'new' | 'outlook' | 'excel';

type AddCandidate = { values: Record<string, string>; existingUserId?: string };

type UserAttributeMapping = {
    InternalName: string;
    DisplayName?: Record<string, string>;
    ReadOnly?: boolean;
    FieldType?: string;
    CognitiveSearch?: {
        Filterable?: boolean;
        Sortable?: boolean;
        Facetable?: boolean;
        Searchable?: boolean;
    };
    Views?: {
        Details?: {
            Sequence?: number;
            Editable?: boolean;
            Required?: boolean;
        };
    };
};

type ReviewField = {
    key: string;
    label: string;
    required: boolean;
    readOnly: boolean;
    sequence: number;
};

/**
 * The per-row membership state surfaced in the review table's Status column.
 * `invite` (net-new user), `add` (existing user, not yet a member) and
 * `already` (existing member — excluded from submit) mirror the legacy
 * directory's `internalStatus`.
 */
type RowStatus = 'invite' | 'add' | 'already';

/**
 * EumUserToolbar
 *
 * A reusable management toolbar for EUM user surfaces. It renders the bulk
 * actions (add users, email all, copy all, export to Excel) into a host-owned
 * mount element and owns the multi-mode "Add Users" modal (add existing, enter
 * new, paste from Outlook, paste from Excel).
 *
 * Extracted from `eum-user-management` so the same toolbar can be mounted
 * standalone or hosted inside `eum-member-directory`. All surfaces are
 * capability-gated via {@link UserToolbarHost.capabilities}.
 */
export class EumUserToolbar {
    private toolbarEl: HTMLElement | null = null;
    private addModalEl: HTMLElement | null = null;
    private addMode: AddMode = 'existing';
    /** True while a paste-pane "Add to queue" run is in flight; keeps the
     *  footer's Add-to-queue button forced-disabled through the whole run. */
    private queueingPaste = false;
    private readonly existingQueue = new Map<string, { id: string; displayName: string; mail: string }>();
    private reviewFields: ReviewField[] = [];

    constructor(private readonly host: UserToolbarHost) {}

    /** True when at least one toolbar action is available to the current user. */
    get hasAnyAction(): boolean {
        const c = this.host.capabilities;
        return c.canAddUsers || c.canEmailUsers || c.canCopyUsers || c.canExportUsers;
    }

    /**
     * Render the toolbar buttons into the provided mount element and, when the
     * add capability is present, inject and wire the add-user modal into the
     * supplied modal container (defaults to the mount's shadow-root host root).
     */
    mount(toolbarMount: HTMLElement, modalContainer: HTMLElement): void {
        this.toolbarEl = toolbarMount;
        this.renderToolbar();

        if (this.host.capabilities.canAddUsers) {
            modalContainer.insertAdjacentHTML('beforeend', this.addModalTemplate());
            this.addModalEl = this.host.shadow.querySelector('.member-add-modal');
            if (this.addModalEl) this.host.modals.register(this.addModalEl, { onHide: () => this.resetAddModal() });
            this.bindAddModalEvents();
        }
    }

    // ── Toolbar ─────────────────────────────────────────────────────────

    private renderToolbar(): void {
        if (!this.toolbarEl) return;
        const c = this.host.capabilities;

        const buttons: string[] = [];
        if (c.canAddUsers) {
            const label = this.host.escapeAttr(i18n.t('userToolbar.add'));
            buttons.push(
                `<button type="button" class="icon-btn toolbar-add" title="${label}" aria-label="${label}">${icon('person-plus')}</button>`
            );
        }
        if (c.canEmailUsers) {
            const label = this.host.escapeAttr(i18n.t('userToolbar.email'));
            buttons.push(
                `<button type="button" class="icon-btn toolbar-email" title="${label}" aria-label="${label}" disabled>${icon('envelope')}</button>`
            );
        }
        if (c.canCopyUsers) {
            const label = this.host.escapeAttr(i18n.t('userToolbar.copy'));
            buttons.push(
                `<button type="button" class="icon-btn toolbar-copy" title="${label}" aria-label="${label}" disabled>${icon('clipboard')}</button>`
            );
        }
        if (c.canExportUsers) {
            const label = this.host.escapeAttr(i18n.t('userToolbar.export'));
            buttons.push(
                `<button type="button" class="icon-btn toolbar-export" title="${label}" aria-label="${label}" disabled>${icon('file-earmark-spreadsheet')}</button>`
            );
        }

        this.toolbarEl.innerHTML = buttons.join('');

        this.toolbarEl.querySelector('.toolbar-add')?.addEventListener('click', () => this.openAddModal());
        this.toolbarEl.querySelector('.toolbar-email')?.addEventListener('click', () => this.emailUsers());
        this.toolbarEl.querySelector('.toolbar-copy')?.addEventListener('click', () => this.copyUsers());
        this.toolbarEl.querySelector('.toolbar-export')?.addEventListener('click', () => this.exportUsers());
    }

    /** Enable/disable the result-set bulk actions based on whether we have results. */
    updateState(hasResults: boolean): void {
        if (!this.toolbarEl) return;
        const disabled = !hasResults;
        ['.toolbar-email', '.toolbar-copy', '.toolbar-export'].forEach((sel) => {
            const btn = this.toolbarEl!.querySelector(sel) as HTMLButtonElement | null;
            if (btn) btn.disabled = disabled;
        });
    }

    /**
     * Every email for the bulk action, de-duplicated and order-preserving.
     *
     * Matches the legacy directory: when a group context exists the addresses
     * come from the group's full membership (`groups/{id}/members`), not just
     * the current on-page/filtered result set. Falls back to the current
     * results only when no group is configured.
     */
    private async collectEmails(): Promise<string[]> {
        const docs = await this.collectMemberDocuments();
        const seen = new Set<string>();
        const emails: string[] = [];
        for (const doc of docs) {
            const email = this.host.emailOf(doc).trim();
            if (email && !seen.has(email.toLowerCase())) {
                seen.add(email.toLowerCase());
                emails.push(email);
            }
        }
        return emails;
    }

    /**
     * Resolve the member documents the bulk actions operate on. Prefers the
     * group's full membership when a group id is present; otherwise uses the
     * host's current filtered results.
     */
    private async collectMemberDocuments(): Promise<Array<Record<string, any>>> {
        if (this.host.groupId) {
            try {
                const result = await this.host.groupService.getMembers<{ data?: Array<Record<string, any>> }>(
                    this.host.groupId
                );
                const members = Array.isArray(result?.data) ? result.data : [];
                if (members.length) return members;
            } catch {
                this.host.toast.error(i18n.t('userToolbar.toast.loadMembersFailed'));
                return [];
            }
        }
        return this.host.getCurrentResults().map((item) => item.document);
    }

    private async emailUsers(): Promise<void> {
        const emails = await this.collectEmails();
        if (!emails.length) {
            this.host.toast.info(i18n.t('userToolbar.toast.noEmails'));
            return;
        }
        window.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(';'))}`;
    }

    private async copyUsers(): Promise<void> {
        const emails = await this.collectEmails();
        if (!emails.length) {
            this.host.toast.info(i18n.t('userToolbar.toast.noEmails'));
            return;
        }
        const text = emails.join('; ');
        try {
            await navigator.clipboard.writeText(text);
            this.host.toast.success(i18n.t('userToolbar.toast.copySucceeded', { count: emails.length }));
        } catch {
            this.host.toast.error(i18n.t('userToolbar.toast.copyFailed'));
        }
    }

    private async exportUsers(): Promise<void> {
        const btn = this.toolbarEl?.querySelector('.toolbar-export') as HTMLButtonElement | null;
        if (btn) btn.disabled = true;
        try {
            // Payload shape mirrors the legacy directory export: the filter is
            // nested under `searchOptions`, with an `EUMSearchSource` facet and
            // empty select/orderBy. The host's filter is already group-scoped.
            const payload = {
                searchOptions: {
                    filter: this.host.getFilter() || '',
                },
                facets: ['EUMSearchSource'],
                select: [],
                orderBy: [],
            };
            const blob = await this.host.groupService.exportUsers('', payload);
            if (!blob || blob.size === 0) {
                this.host.toast.error(i18n.t('userToolbar.toast.exportEmpty'));
                return;
            }

            const now = new Date();
            const stamp = now.toLocaleString(undefined, { hour12: false }).replace(/,/g, '');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${i18n.t('userToolbar.exportFilename', { stamp })}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            this.host.toast.success(i18n.t('userToolbar.toast.exportSucceeded'));
        } catch {
            this.host.toast.error(i18n.t('userToolbar.toast.exportFailed'));
        } finally {
            if (btn) btn.disabled = false;
        }
    }

    // ── Add-user modal ──────────────────────────────────────────────────

    private addModalTemplate(): string {
        const esc = (key: string) => this.host.escapeHTML(i18n.t(key));
        const escA = (key: string) => this.host.escapeAttr(i18n.t(key));
        return /*html*/ `
            <div class="modal fade member-add-modal add-modal" role="dialog" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${esc('userToolbar.addModal.title')}</h5>
                            <button type="button" class="btn-close add-close" aria-label="${escA('userToolbar.addModal.closeLabel')}"></button>
                        </div>
                        <div class="modal-body">
                            <ul class="nav nav-tabs nav-fill" role="tablist">
                                <li class="nav-item"><button type="button" class="nav-link active" data-add-mode="existing">${esc('userToolbar.addModal.tabExisting')}</button></li>
                                <li class="nav-item"><button type="button" class="nav-link" data-add-mode="outlook">${esc('userToolbar.addModal.tabOutlook')}</button></li>
                                <li class="nav-item"><button type="button" class="nav-link" data-add-mode="excel">${esc('userToolbar.addModal.tabExcel')}</button></li>
                                <li class="nav-item"><button type="button" class="nav-link" data-add-mode="new">${esc('userToolbar.addModal.tabNew')}</button></li>
                            </ul>

                            <div class="add-pane add-pane-existing mt-3">
                                <eum-search-box class="add-search-box"></eum-search-box>
                                <div class="search-add-results mt-2" hidden></div>
                            </div>

                            <div class="add-pane add-pane-new mt-3" hidden>
                                <table class="table new-users-table">
                                    <thead><tr class="new-users-header-row"></tr></thead>
                                    <tbody></tbody>
                                </table>
                                <button type="button" class="btn btn-sm btn-outline-secondary add-new-row">${icon('plus-lg')}<span>${esc('userToolbar.addModal.addRow')}</span></button>
                            </div>

                            <div class="add-pane add-pane-outlook mt-3" hidden>
                                <p class="text-body-secondary small mb-1">${esc('userToolbar.addModal.outlookHint')}</p>
                                <textarea rows="10" class="form-control paste-area paste-outlook"></textarea>
                            </div>

                            <div class="add-pane add-pane-excel mt-3" hidden>
                                <p class="text-body-secondary small mb-1">${esc('userToolbar.addModal.excelHint')}</p>
                                <textarea rows="10" class="form-control paste-area paste-excel"></textarea>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <div class="add-footer-status small me-auto text-body-secondary" role="status" aria-live="polite"></div>
                            <button type="button" class="btn btn-outline-primary add-to-queue" hidden>${esc('userToolbar.addModal.addToQueue')}</button>
                            <button type="button" class="btn btn-primary add-submit">${esc('userToolbar.addModal.submit')}</button>
                            <button type="button" class="btn btn-outline-secondary add-cancel">${esc('userToolbar.addModal.close')}</button>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    private bindAddModalEvents(): void {
        const modal = this.addModalEl;
        if (!modal) return;

        modal.querySelectorAll<HTMLButtonElement>('[data-add-mode]').forEach((tab) => {
            tab.addEventListener('click', () => this.switchAddMode(tab.dataset.addMode as AddMode));
        });

        modal.querySelector('.add-search-box')?.addEventListener('eum-search', ((e: Event) => {
            // Keep this modal-scoped search from bubbling to the host's
            // eum-search-base listener, which would drive the main page search
            // and mutate the URL query params.
            e.stopPropagation();
            const detail = (e as CustomEvent<{ searchText: string }>).detail;
            this.runAddSearch(detail?.searchText ?? '');
        }) as EventListener);

        modal.querySelector('.add-new-row')?.addEventListener('click', () => this.addNewUserRow());
        modal.querySelector('.add-submit')?.addEventListener('click', () => this.submitAdd());

        // Secondary paste-pane action: queue the parsed users without leaving
        // the current tab (clears the textarea and reports the count).
        modal.querySelector('.add-to-queue')?.addEventListener('click', () => {
            if (this.addMode === 'outlook' || this.addMode === 'excel') {
                void this.queuePastedUsers(this.addMode, { stayOnTab: true });
            }
        });

        // Close (X) and Cancel are guarded: when the queue is non-empty they
        // prompt the discard confirmation instead of closing immediately.
        modal.querySelector('.add-close')?.addEventListener('click', () => void this.requestCloseAddModal());
        modal.querySelector('.add-cancel')?.addEventListener('click', () => void this.requestCloseAddModal());

        // Keep the paste-pane footer button counts live as the user types.
        modal.querySelector('.paste-outlook')?.addEventListener('input', () => {
            if (this.addMode === 'outlook') this.refreshPasteState();
        });
        modal.querySelector('.paste-excel')?.addEventListener('input', () => {
            if (this.addMode === 'excel') this.refreshPasteState();
        });
    }

    private async openAddModal(): Promise<void> {
        const modal = this.addModalEl;
        if (!modal || !this.host.capabilities.canAddUsers) return;

        await this.ensureReviewFields();
        this.renderReviewHeader();

        this.existingQueue.clear();
        this.switchAddMode('existing');
        const searchResults = modal.querySelector('.search-add-results') as HTMLElement | null;
        if (searchResults) {
            searchResults.innerHTML = '';
            searchResults.hidden = true;
        }
        const searchBox = modal.querySelector('.add-search-box');
        if (searchBox) (searchBox as any).clear?.();
        const tbody = modal.querySelector('.new-users-table tbody');
        if (tbody) tbody.innerHTML = '';
        this.addNewUserRow();
        this.ensureTrailingEmptyRow();
        const outlook = modal.querySelector('.paste-outlook') as HTMLTextAreaElement | null;
        if (outlook) outlook.value = '';
        const excel = modal.querySelector('.paste-excel') as HTMLTextAreaElement | null;
        if (excel) excel.value = '';
        this.updateQueueStatus();

        this.host.modals.show(modal);
    }

    /** True when the review queue currently holds at least one pending user. */
    private hasPendingWork(): boolean {
        if (this.existingQueue.size) return true;
        // Any manual/pasted row with content (that isn't already a member) is
        // also unsaved work worth guarding against.
        return this.getReviewRows().some(
            (row) => !this.isManualEmptyRow(row) && row.dataset.rowStatus !== 'already'
        );
    }

    /**
     * Guarded close for the Add Users modal. When there is pending queued work,
     * a confirmation prompt (via the shared PromptManager) is layered over the
     * modal; otherwise the modal closes immediately.
     */
    private async requestCloseAddModal(): Promise<void> {
        const modal = this.addModalEl;
        if (!modal) return;

        if (this.hasPendingWork()) {
            const count = this.existingQueue.size || this.getReviewRows().filter(
                (row) => !this.isManualEmptyRow(row) && row.dataset.rowStatus !== 'already'
            ).length;
            const confirmed = await this.host.prompts.confirm({
                title: i18n.t('userToolbar.addModal.discard.title'),
                body: i18n.t('userToolbar.addModal.discard.body', { count }),
                confirmLabel: i18n.t('userToolbar.addModal.discard.confirm'),
                cancelLabel: i18n.t('userToolbar.addModal.discard.cancel'),
                danger: true,
            });
            if (!confirmed) return;
        }

        this.host.modals.hide(modal);
    }

    /**
     * Reset the Add Users modal to a pristine state. Invoked whenever the modal
     * is hidden so the next open starts clean (search box value, queue, review
     * rows, paste areas, footer status).
     */
    private resetAddModal(): void {
        const modal = this.addModalEl;
        if (!modal) return;

        this.existingQueue.clear();

        const searchResults = modal.querySelector('.search-add-results') as HTMLElement | null;
        if (searchResults) {
            searchResults.innerHTML = '';
            searchResults.hidden = true;
        }
        const searchBox = modal.querySelector('.add-search-box');
        if (searchBox) (searchBox as any).clear?.();

        const tbody = modal.querySelector('.new-users-table tbody');
        if (tbody) tbody.innerHTML = '';

        const outlook = modal.querySelector('.paste-outlook') as HTMLTextAreaElement | null;
        if (outlook) outlook.value = '';
        const excel = modal.querySelector('.paste-excel') as HTMLTextAreaElement | null;
        if (excel) excel.value = '';

        this.setFooterStatus('');
    }

    private switchAddMode(mode: AddMode): void {
        this.addMode = mode;
        const modal = this.addModalEl;
        if (!modal) return;
        modal.querySelectorAll<HTMLButtonElement>('[data-add-mode]').forEach((tab) => {
            tab.classList.toggle('active', tab.dataset.addMode === mode);
        });
        const panes: Record<AddMode, string> = {
            existing: '.add-pane-existing',
            new: '.add-pane-new',
            outlook: '.add-pane-outlook',
            excel: '.add-pane-excel',
        };
        (Object.keys(panes) as AddMode[]).forEach((key) => {
            const pane = modal.querySelector(panes[key]) as HTMLElement | null;
            if (pane) pane.hidden = key !== mode;
        });

        // Clear any stale status from a previous tab; updateQueueStatus/
        // refreshPasteState re-add the appropriate summary for this tab.
        this.setFooterStatus('');
        this.updateFooterButtons();
        if (mode === 'outlook' || mode === 'excel') {
            this.refreshPasteState();
        } else {
            this.updateQueueStatus();
        }
    }

    /**
     * Write a message into the footer's left status zone. In-modal status
     * (queuing counts, "no users", errors, per-item progress) lives here rather
     * than in a toast, since a toast would render on top of the open modal.
     * Pass an empty string to clear. `tone` selects the Bootstrap text colour.
     */
    private setFooterStatus(message: string, tone: 'muted' | 'danger' | 'success' | 'primary' = 'muted'): void {        const el = this.addModalEl?.querySelector('.add-footer-status') as HTMLElement | null;
        if (!el) return;
        const toneClass =
            tone === 'danger'
                ? 'text-danger'
                : tone === 'success'
                  ? 'text-success'
                  : tone === 'primary'
                    ? 'text-primary'
                    : 'text-body-secondary';
        el.className = `add-footer-status small me-auto ${toneClass}`;
        el.textContent = message;
    }

    /**
     * Enable/disable the modal's close controls (Cancel button and header X)
     * while an add run is in progress, so the sequential submit can't be
     * interrupted partway through.
     */
    private setCloseControlsDisabled(disabled: boolean): void {
        const modal = this.addModalEl;
        if (!modal) return;
        modal.querySelectorAll<HTMLButtonElement>('.add-cancel, .add-close').forEach((btn) => {
            btn.disabled = disabled;
        });
    }

    /** Compute the primary footer button label for the given add mode. */
    private submitLabelFor(mode: AddMode): string {
        // Review & Add Users — verbose, count-aware final action.
        if (mode === 'new') {
            return i18n.t('userToolbar.addModal.reviewSubmit', { count: this.collectNewUsers().length });
        }
        // Find Existing / Paste panes — the primary button navigates to the
        // Review & Add Users tab and reflects the total queued for review.
        return i18n.t('userToolbar.addModal.reviewQueue', { count: this.reviewQueueCount() });
    }

    /** Number of users currently queued in the Review table (excludes the
     *  trailing empty row and rows already in the group). */
    private reviewQueueCount(): number {
        return this.getReviewRows().filter(
            (row) => !this.isManualEmptyRow(row) && row.dataset.rowStatus !== 'already'
        ).length;
    }

    /**
     * Show/label the footer's primary (Review queue) and secondary (Add to
     * queue) buttons for the current tab. The secondary button only appears on
     * the paste tabs, immediately left of the primary.
     */
    private updateFooterButtons(): void {
        const modal = this.addModalEl;
        if (!modal) return;

        const submitBtn = modal.querySelector('.add-submit') as HTMLButtonElement | null;
        if (submitBtn) submitBtn.textContent = this.submitLabelFor(this.addMode);

        const queueBtn = modal.querySelector('.add-to-queue') as HTMLButtonElement | null;
        if (queueBtn) {
            const isPaste = this.addMode === 'outlook' || this.addMode === 'excel';
            queueBtn.hidden = !isPaste;
            if (this.addMode === 'outlook' || this.addMode === 'excel') {
                const count = this.parsePasteCount(this.addMode);
                queueBtn.textContent = i18n.t('userToolbar.addModal.addToQueue', { count });
                // Stay disabled while a queue run is in flight, regardless of
                // the current textarea contents.
                queueBtn.disabled = this.queueingPaste || count === 0;
            }
        }
    }

    /**
     * Live-update paste-pane UI as the user edits the textarea: refresh the
     * "Add to queue (n)" button and mirror the same pre-add status verbiage
     * that Find Existing uses in the footer status zone.
     */
    private refreshPasteState(): void {
        if (this.addMode !== 'outlook' && this.addMode !== 'excel') return;
        this.updateFooterButtons();
        const count = this.parsePasteCount(this.addMode);
        this.setFooterStatus(count ? i18n.t('userToolbar.addModal.queueSummary', { count }) : '');
    }

    /** Count the parseable candidates currently in a paste pane. */
    private parsePasteCount(mode: 'outlook' | 'excel'): number {
        const modal = this.addModalEl;
        if (!modal) return 0;
        const selector = mode === 'outlook' ? '.paste-outlook' : '.paste-excel';
        const raw = (modal.querySelector(selector) as HTMLTextAreaElement | null)?.value ?? '';
        return mode === 'outlook' ? this.parseOutlook(raw).length : this.parseExcel(raw).length;
    }

    /** Refresh the footer button labels for the current mode. */
    private refreshSubmitLabel(): void {
        this.updateFooterButtons();
    }

    private async ensureReviewFields(): Promise<void> {
        if (this.reviewFields.length) return;
        const mappings = await this.fetchUserAttributesMapping();
        this.reviewFields = this.mapReviewFields(mappings);
    }

    private async fetchUserAttributesMapping(): Promise<UserAttributeMapping[]> {
        const endpoint = this.host.apiBaseUrl
            ? `${this.host.apiBaseUrl.replace(/\/$/, '')}/eum_api/v1/configuration`
            : '/api/EUMAPI/Get?apiUrl=configuration';
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json; charset=utf-8' };
            const token = (window as any).EUMPortal?.bearerToken;
            if (token) headers.Authorization = token;

            const response = await fetch(endpoint, {
                method: 'GET',
                headers,
            });
            if (!response.ok) return [];
            const payload = await response.json();
            const raw = payload?.UserAttributesMapping;
            if (typeof raw !== 'string') return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private mapReviewFields(mappings: UserAttributeMapping[]): ReviewField[] {
        const locale = i18n.locale?.split('-')[0] || 'en';
        const fields = mappings
            .filter((mapping) => {
                if (!mapping || !mapping.InternalName) return false;
                if (mapping.InternalName === 'mail') return true;
                if (!mapping.Views?.Details?.Editable || !(Number(mapping.Views?.Details?.Sequence ?? 0) > 0)) {
                    return false;
                }
                // Mirror the legacy directory: only attributes that participate
                // in Cognitive Search are shown as editable columns. This is what
                // excludes virtual pickers such as the Groups field
                // (`InitialGroups`, a grouppicker with no search flags).
                const cs = mapping.CognitiveSearch;
                return !!cs && (!!cs.Filterable || !!cs.Sortable || !!cs.Facetable || !!cs.Searchable);
            })
            .map((mapping) => {
                const key = mapping.InternalName;
                const displayName = mapping.DisplayName ?? {};
                const label = String(displayName[locale] ?? displayName.en ?? key);
                const sequence = key === 'mail' ? -1 : Number(mapping.Views?.Details?.Sequence ?? Number.MAX_SAFE_INTEGER);
                return {
                    key,
                    label,
                    required: !!mapping.Views?.Details?.Required,
                    // User Status is always read-only on the Add Users UI; it is
                    // derived (existing users keep their status, manual entries
                    // default to "Active" once an email is provided).
                    readOnly: key === 'UserStatus' ? true : !!mapping.ReadOnly,
                    sequence,
                } as ReviewField;
            })
            .sort((a, b) => a.sequence - b.sequence || a.key.localeCompare(b.key));

        if (!fields.some((f) => f.key === 'mail')) {
            fields.unshift({ key: 'mail', label: 'Email', required: true, readOnly: false, sequence: -1 });
        }
        return fields;
    }

    private renderReviewHeader(): void {
        const headerRow = this.addModalEl?.querySelector('.new-users-header-row') as HTMLElement | null;
        if (!headerRow) return;
        headerRow.innerHTML = `<th></th><th>${this.host.escapeHTML(i18n.t('userToolbar.addModal.status.column'))}</th>${this.reviewFields
            .map((field) => {
                const marker = field.required
                    ? `<span class="required-marker" aria-hidden="true" title="${this.host.escapeAttr(i18n.t('userToolbar.addModal.validation.required'))}">*</span>`
                    : '';
                return `<th>${this.host.escapeHTML(field.label)}${marker}</th>`;
            })
            .join('')}`;
    }

    private primaryEmailKey(): string {
        const explicit = this.reviewFields.find((f) => f.key === 'mail')?.key;
        if (explicit) return explicit;
        return this.reviewFields.find((f) => /mail|email/i.test(f.key))?.key ?? 'mail';
    }

    private firstNameKey(): string | null {
        return this.reviewFields.find((f) => ['givenName', 'firstName'].includes(f.key))?.key ?? null;
    }

    private lastNameKey(): string | null {
        return this.reviewFields.find((f) => ['surname', 'lastName'].includes(f.key))?.key ?? null;
    }

    private displayNameKey(): string | null {
        return this.reviewFields.find((f) => f.key === 'displayName')?.key ?? null;
    }

    private userStatusKey(): string | null {
        return this.reviewFields.find((f) => f.key === 'UserStatus')?.key ?? null;
    }

    private splitDisplayName(displayName: string): { first: string; last: string } {
        const parts = displayName.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return { first: '', last: '' };
        if (parts.length === 1) return { first: parts[0], last: '' };
        return { first: parts[0], last: parts.slice(1).join(' ') };
    }

    private fieldInput(row: HTMLTableRowElement, key: string): HTMLInputElement | null {
        const inputs = Array.from(row.querySelectorAll<HTMLInputElement>('input[data-field-key]'));
        return inputs.find((input) => input.dataset.fieldKey === key) ?? null;
    }

    /**
     * Validate a single field on a review row, toggling Bootstrap's
     * `.is-invalid` state and the adjacent `.field-error` message. Empty
     * required fields and malformed emails are flagged. The trailing empty
     * placeholder row is skipped so it never shows errors. Returns true when
     * the field is valid.
     */
    private validateField(row: HTMLTableRowElement, field: ReviewField): boolean {
        const input = this.fieldInput(row, field.key);
        if (!input) return true;

        // Never flag the trailing, entirely-empty placeholder row.
        if (this.isManualEmptyRow(row)) {
            this.setFieldError(row, field.key, '');
            return true;
        }

        const value = input.value.trim();
        let message = '';
        if (field.required && !value) {
            message = i18n.t('userToolbar.addModal.validation.required');
        } else if (value && field.key === this.primaryEmailKey() && !this.isValidEmail(value)) {
            message = i18n.t('userToolbar.addModal.validation.invalidEmail');
        }

        this.setFieldError(row, field.key, message);
        return !message;
    }

    /** Toggle the invalid state + inline error text for a field cell. */
    private setFieldError(row: HTMLTableRowElement, key: string, message: string): void {
        const input = this.fieldInput(row, key);
        const errorEl = row.querySelector(`.field-error[data-error-for="${CSS.escape(key)}"]`) as HTMLElement | null;
        if (input) input.classList.toggle('is-invalid', !!message);
        if (errorEl) errorEl.textContent = message;
    }

    private isValidEmail(value: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    /** Validate every editable field on a row; returns true when all pass. */
    private validateRow(row: HTMLTableRowElement): boolean {
        if (this.isManualEmptyRow(row)) return true;
        let valid = true;
        this.reviewFields.forEach((field) => {
            if (field.readOnly) return;
            if (!this.validateField(row, field)) valid = false;
        });
        return valid;
    }

    private addNewUserRow(prefill?: { values?: Record<string, string>; existingUserId?: string }): HTMLTableRowElement | null {
        const tbody = this.addModalEl?.querySelector('.new-users-table tbody');
        if (!tbody || !this.reviewFields.length) return null;

        const tr = document.createElement('tr');
        tr.innerHTML = `<td class="new-remove-cell"><button type="button" class="icon-btn icon-btn-secondary new-remove" aria-label="${this.host.escapeAttr(i18n.t('userToolbar.addModal.removeRow'))}">${icon('dash-lg')}</button></td><td class="new-status"><span class="row-status text-body-secondary small"></span></td>${this.reviewFields
            .map((field) => {
                const type = field.key === this.primaryEmailKey() ? 'email' : 'text';
                return `<td><input type="${type}" class="form-control form-control-sm" data-field-key="${this.host.escapeAttr(field.key)}"><div class="field-error invalid-feedback" data-error-for="${this.host.escapeAttr(field.key)}"></div></td>`;
            })
            .join('')}`;

        this.populateReviewRow(tr, prefill);

        tr.querySelector('.new-remove')?.addEventListener('click', () => this.removeReviewRow(tr));
        const email = this.fieldInput(tr, this.primaryEmailKey());
        email?.addEventListener('blur', () => {
            this.applyManualRowState(tr);
            this.ensureTrailingEmptyRow();
            this.updateRemoveButtons();
            void this.checkManualRowMembership(tr);
        });

        const firstKey = this.firstNameKey();
        const lastKey = this.lastNameKey();
        const displayKey = this.displayNameKey();
        if (firstKey) this.fieldInput(tr, firstKey)?.addEventListener('input', () => this.syncDerivedDisplayName(tr));
        if (lastKey) this.fieldInput(tr, lastKey)?.addEventListener('input', () => this.syncDerivedDisplayName(tr));
        if (displayKey) {
            this.fieldInput(tr, displayKey)?.addEventListener('input', () => {
                tr.dataset.displayNameManual = 'true';
            });
        }

        // Live per-field validation: validate on blur (focus-out) so the user
        // isn't nagged mid-typing, then clear the error live as they correct it.
        this.reviewFields.forEach((field) => {
            const input = this.fieldInput(tr, field.key);
            if (!input || field.readOnly) return;
            input.addEventListener('blur', () => this.validateField(tr, field));
            input.addEventListener('input', () => {
                if (input.classList.contains('is-invalid')) this.validateField(tr, field);
            });
        });

        tbody.appendChild(tr);
        this.updateRemoveButtons();
        return tr;
    }

    private populateReviewRow(row: HTMLTableRowElement, values?: { values?: Record<string, string>; existingUserId?: string }): void {
        const rowValues = values?.values ?? {};
        this.reviewFields.forEach((field) => {
            const input = this.fieldInput(row, field.key);
            if (input) input.value = String(rowValues[field.key] ?? '');
        });

        row.dataset.displayNameManual = 'false';
        if (values?.existingUserId) row.dataset.existingUserId = values.existingUserId;
        else delete row.dataset.existingUserId;

        this.syncDerivedDisplayName(row, true);
        this.applyManualRowState(row);

        // A row backed by a user queued from the search results is, by
        // definition, an existing user being added to the group.
        if (values?.existingUserId) {
            this.setRowStatus(row, 'add');
        }
    }

    /**
     * Render the Status column for a review row and stamp `data-row-status`
     * (used to gate submit). `invite` = net-new user, `add` = existing user not
     * yet in the group, `already` = existing member (excluded from submit and
     * locked). Passing `null` clears the cell (empty placeholder rows).
     */
    private setRowStatus(row: HTMLTableRowElement, status: RowStatus | null): void {
        const label = row.querySelector('.row-status') as HTMLElement | null;
        if (status) row.dataset.rowStatus = status;
        else delete row.dataset.rowStatus;

        if (!label) return;
        if (!status) {
            label.textContent = '';
            label.className = 'row-status text-body-secondary small';
            return;
        }

        const key: Record<RowStatus, string> = {
            invite: 'userToolbar.addModal.status.invite',
            add: 'userToolbar.addModal.status.addToGroup',
            already: 'userToolbar.addModal.status.alreadyInGroup',
        };
        const tone: Record<RowStatus, string> = {
            invite: 'text-primary',
            add: 'text-success',
            already: 'text-body-secondary',
        };
        label.textContent = i18n.t(key[status]);
        label.className = `row-status small ${tone[status]}`;
        if (this.addMode === 'new') this.refreshSubmitLabel();
    }

    /**
     * Reflect a submit-time progress transition on the row's Status column
     * (`inviting`/`adding` in-flight, `invited`/`added` on success, `error` on
     * failure). Mirrors the legacy directory's status labels during processing.
     */
    private setRowProgress(
        row: HTMLTableRowElement,
        progress: 'inviting' | 'adding' | 'invited' | 'added' | 'error'
    ): void {
        const label = row.querySelector('.row-status') as HTMLElement | null;
        if (!label) return;
        const key = `userToolbar.addModal.status.${progress}`;
        const tone =
            progress === 'error'
                ? 'text-danger'
                : progress === 'invited' || progress === 'added'
                  ? 'text-success'
                  : 'text-body-secondary';
        label.textContent = i18n.t(key);
        label.className = `row-status small ${tone}`;
    }

    /**
     * On manual email blur, check whether the entered address already maps to an
     * existing user and whether that user is already a member of the group. This
     * mirrors the legacy directory's `checkUserData`: existing-but-not-member
     * rows become "Add to group", existing members become "Already in group"
     * (locked), and unknown addresses become "Invite user".
     */
    private async checkManualRowMembership(row: HTMLTableRowElement): Promise<void> {
        if (row.dataset.existingUserId) return;

        const emailInput = this.fieldInput(row, this.primaryEmailKey());
        const mail = emailInput?.value.trim() ?? '';
        if (!mail) {
            this.setRowStatus(row, null);
            return;
        }

        const label = row.querySelector('.row-status') as HTMLElement | null;
        if (label) {
            label.textContent = i18n.t('userToolbar.addModal.status.checking');
            label.className = 'row-status text-body-secondary small';
        }

        // Guard against stale responses when the address changes mid-flight.
        const token = mail.toLowerCase();
        row.dataset.membershipToken = token;

        const escaped = mail.replace(/'/g, "''");
        const searchModule = await import('../../core/services/search-service.js');
        try {
            const existsFilter = `EUMSearchSource eq 'user' and mail eq '${escaped}'`;
            const memberFilter = this.host.groupId
                ? `${existsFilter} and MemberGroups/any(g: g eq '${this.host.groupId.replace(/'/g, "''")}')`
                : null;

            const [existsResult, memberResult] = await Promise.all([
                searchModule.searchService.search(this.host.searchUrl, '', {
                    EUMSearchSources: ['user'],
                    Select: Array.from(new Set([...this.reviewFields.map((f) => f.key), 'id', 'mail'])),
                    searchOptions: { size: 1, skip: 0, includeTotalCount: false, filter: existsFilter },
                }),
                memberFilter
                    ? searchModule.searchService.search(this.host.searchUrl, '', {
                          EUMSearchSources: ['user'],
                          Select: ['id', 'mail'],
                          searchOptions: { size: 1, skip: 0, includeTotalCount: false, filter: memberFilter },
                      })
                    : Promise.resolve({ totalCount: 0, value: [] as Array<{ document: Record<string, any> }> }),
            ]);

            // Ignore if the email changed while the request was in flight.
            if (row.dataset.membershipToken !== token) return;

            const existingDoc = existsResult.value?.[0]?.document as Record<string, any> | undefined;
            const userInGroup = (memberResult.value?.length ?? 0) >= 1;

            if (!existingDoc) {
                this.setRowStatus(row, 'invite');
                return;
            }

            const userId = String(existingDoc.id ?? '').trim();
            if (userInGroup) {
                // Already a member: reflect the existing values, lock the row and
                // exclude it from submit. Do not queue it.
                const values: Record<string, string> = {};
                this.reviewFields.forEach((field) => {
                    values[field.key] = String(existingDoc[field.key] ?? '');
                });
                this.reviewFields.forEach((field) => {
                    const input = this.fieldInput(row, field.key);
                    if (input) input.value = values[field.key] ?? '';
                });
                this.lockRow(row);
                this.setRowStatus(row, 'already');
                return;
            }

            // Existing user not in the group: promote to a queued "add" row.
            if (userId) {
                const displayName = String(existingDoc.displayName ?? '').trim();
                if (!this.existingQueue.has(userId)) {
                    this.existingQueue.set(userId, { id: userId, displayName, mail });
                }
                const values: Record<string, string> = {};
                this.reviewFields.forEach((field) => {
                    values[field.key] = String(existingDoc[field.key] ?? '');
                });
                this.populateReviewRow(row, { values, existingUserId: userId });
                this.updateQueueStatus();
                this.updateRemoveButtons();
            } else {
                this.setRowStatus(row, 'invite');
            }
        } catch {
            if (row.dataset.membershipToken !== token) return;
            // Non-blocking: default to invite when the lookup fails.
            this.setRowStatus(row, 'invite');
        }
    }

    /** Disable every input in a row (used for already-in-group rows). */
    private lockRow(row: HTMLTableRowElement): void {
        row.querySelectorAll<HTMLInputElement>('input[data-field-key]').forEach((input) => {
            input.disabled = true;
        });
    }

    private applyManualRowState(row: HTMLTableRowElement): void {
        const hasExistingUser = !!row.dataset.existingUserId;
        const emailInput = this.fieldInput(row, this.primaryEmailKey());
        const hasEmail = !!emailInput?.value.trim();
        const enableNonEmailFields = hasExistingUser || hasEmail;

        this.reviewFields.forEach((field) => {
            const input = this.fieldInput(row, field.key);
            if (!input) return;

            if (field.key === this.primaryEmailKey()) {
                input.disabled = !!(hasExistingUser || field.readOnly);
                return;
            }

            const disabled = field.readOnly || !enableNonEmailFields;
            input.disabled = disabled;
            if (disabled && !hasExistingUser && !hasEmail) {
                input.value = '';
            }
        });

        // User Status stays read-only. For a manually-entered user (no existing
        // user backing the row) it defaults to "Active" once an email has been
        // provided, and clears again when the row is emptied.
        const statusKey = this.userStatusKey();
        if (statusKey && !hasExistingUser) {
            const statusInput = this.fieldInput(row, statusKey);
            if (statusInput) statusInput.value = hasEmail ? 'Active' : '';
        }

        if (!enableNonEmailFields && !hasExistingUser) {
            row.dataset.displayNameManual = 'false';
        }
    }

    private syncDerivedDisplayName(row: HTMLTableRowElement, force = false): void {
        const displayKey = this.displayNameKey();
        const firstKey = this.firstNameKey();
        const lastKey = this.lastNameKey();
        if (!displayKey || !firstKey || !lastKey) return;
        if (!force && row.dataset.displayNameManual === 'true') return;

        const first = this.fieldInput(row, firstKey)?.value.trim() ?? '';
        const last = this.fieldInput(row, lastKey)?.value.trim() ?? '';
        const display = this.fieldInput(row, displayKey);
        if (!display) return;
        display.value = [first, last].filter(Boolean).join(' ').trim();
    }

    private getReviewRows(): HTMLTableRowElement[] {
        const tbody = this.addModalEl?.querySelector('.new-users-table tbody');
        if (!tbody) return [];
        return Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
    }

    private rowValues(row: HTMLTableRowElement): Record<string, string> {
        const values: Record<string, string> = {};
        this.reviewFields.forEach((field) => {
            const value = this.fieldInput(row, field.key)?.value ?? '';
            values[field.key] = value.trim();
        });
        return values;
    }

    private isManualEmptyRow(row: HTMLTableRowElement): boolean {
        if (row.dataset.existingUserId) return false;
        const values = this.rowValues(row);
        return this.reviewFields.every((field) => !(values[field.key] ?? '').trim());
    }

    private removeReviewRow(row: HTMLTableRowElement): void {
        if (this.isProtectedPlaceholderRow(row)) return;

        const existingUserId = row.dataset.existingUserId;
        if (existingUserId) {
            this.existingQueue.delete(existingUserId);
            this.updateQueueStatus();
        }

        row.remove();
        this.ensureTrailingEmptyRow();
        this.updateRemoveButtons();
    }

    private isProtectedPlaceholderRow(row: HTMLTableRowElement): boolean {
        const rows = this.getReviewRows();
        const lastRow = rows[rows.length - 1];
        return row === lastRow && this.isManualEmptyRow(row);
    }

    private ensureTrailingEmptyRow(): void {
        const rows = this.getReviewRows();
        if (!rows.length) {
            this.addNewUserRow();
            return;
        }

        const lastRow = rows[rows.length - 1];
        const keepLastEmpty = this.isManualEmptyRow(lastRow) ? lastRow : null;
        rows.forEach((row) => {
            if (row !== keepLastEmpty && this.isManualEmptyRow(row)) row.remove();
        });

        const refreshedRows = this.getReviewRows();
        const newLastRow = refreshedRows[refreshedRows.length - 1];
        if (!newLastRow || !this.isManualEmptyRow(newLastRow)) {
            this.addNewUserRow();
        }
    }

    private updateRemoveButtons(): void {
        const rows = this.getReviewRows();
        const lastRow = rows[rows.length - 1];
        const onlyRow = rows.length === 1;
        rows.forEach((row) => {
            const btn = row.querySelector('.new-remove') as HTMLButtonElement | null;
            if (!btn) return;
            // Lock the trailing empty placeholder, and lock the sole remaining
            // row (nothing to remove when there's only one).
            const locked = onlyRow || (row === lastRow && this.isManualEmptyRow(row));
            btn.disabled = locked;
            btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
        });
        if (this.addMode === 'new') this.refreshSubmitLabel();
    }

    private async hydrateExistingRow(row: HTMLTableRowElement, userId: string): Promise<void> {
        try {
            const select = Array.from(new Set([...this.reviewFields.map((f) => f.key), 'id', 'mail']));
            const escapedId = userId.replace(/'/g, "''");
            const memberFilter = this.host.groupId
                ? `id eq '${escapedId}' and MemberGroups/any(g: g eq '${this.host.groupId.replace(/'/g, "''")}')`
                : null;

            const searchModule = await import('../../core/services/search-service.js');
            const [results, memberResult] = await Promise.all([
                searchModule.searchService.search(this.host.searchUrl, '', {
                    searchOptions: {
                        size: 1,
                        skip: 0,
                        includeTotalCount: false,
                        filter: `id eq '${escapedId}'`,
                    },
                    Select: select,
                }),
                memberFilter
                    ? searchModule.searchService.search(this.host.searchUrl, '', {
                          searchOptions: { size: 1, skip: 0, includeTotalCount: false, filter: memberFilter },
                          Select: ['id'],
                      })
                    : Promise.resolve({ totalCount: 0, value: [] as Array<{ document: Record<string, any> }> }),
            ]);

            const doc = results.value?.[0]?.document;
            if (!doc) return;

            const values: Record<string, string> = {};
            this.reviewFields.forEach((field) => {
                values[field.key] = String(doc[field.key] ?? '');
            });
            this.populateReviewRow(row, { values, existingUserId: userId });

            // If the user is already a member of the group, surface the
            // "Already in group" state and lock the row so it is excluded from
            // submit. Otherwise it remains an "Add to group" row.
            if ((memberResult.value?.length ?? 0) >= 1) {
                this.lockRow(row);
                this.setRowStatus(row, 'already');
            }
        } catch {
            // Non-blocking: queued row keeps initial values if hydration fails.
        }
    }

    private updateQueueStatus(): void {
        const modal = this.addModalEl;
        if (!modal) return;

        const count = this.existingQueue.size;

        // Reflect the queued count in the footer status zone while on the
        // Find Existing tab; other tabs manage their own footer messaging.
        if (this.addMode === 'existing') {
            this.setFooterStatus(count ? i18n.t('userToolbar.addModal.queueSummary', { count }) : '');
        }

        this.updateFooterButtons();
    }


    private async runAddSearch(searchText: string): Promise<void> {
        const modal = this.addModalEl;
        if (!modal) return;
        const resultsEl = modal.querySelector('.search-add-results') as HTMLElement | null;
        if (!resultsEl) return;

        const text = searchText.trim();
        if (!text) {
            resultsEl.innerHTML = '';
            resultsEl.hidden = true;
            return;
        }

        resultsEl.hidden = false;
        resultsEl.innerHTML = `<div class="p-2 text-body-secondary small">${this.host.escapeHTML(i18n.t('userToolbar.search.searching'))}</div>`;

        try {
            const select = Array.from(new Set([...(this.host.selectFields ?? []), 'id', 'displayName', 'mail', 'companyName', 'HasImage']));
            const results = await (await import('../../core/services/search-service.js')).searchService.search(
                this.host.searchUrl,
                text,
                { searchOptions: { size: 25, skip: 0, includeTotalCount: false, filter: '' }, Select: select, OrderBy: ['displayName asc'] }
            );

            const validResults = results.value
                .map((item) => item.document ?? {})
                .map((doc) => ({
                    id: String(doc.id ?? '').trim(),
                    displayName: String(doc.displayName ?? '').trim(),
                    mail: String(doc.mail ?? '').trim(),
                    companyName: String(doc.companyName ?? doc.CompanyName ?? '').trim(),
                    hasImage: this.host.hasImageOf(doc),
                    imageUrl: this.host.avatarImageUrlOf(doc),
                }))
                .filter((doc) => doc.id && doc.displayName && doc.mail);

            if (!validResults.length) {
                resultsEl.innerHTML = `<div class="p-2 text-body-secondary small">${this.host.escapeHTML(i18n.t('userToolbar.search.noResults'))}</div>`;
                return;
            }

            resultsEl.innerHTML = validResults
                .map((doc) => {
                    const queued = this.existingQueue.has(doc.id);
                    const hint = queued ? '' : this.host.escapeAttr(i18n.t('userToolbar.search.queueHint', { name: doc.displayName }));
                    return /*html*/ `
                        <div class="result-row ${queued ? 'is-queued' : ''}" data-user-id="${this.host.escapeAttr(doc.id)}" data-display-name="${this.host.escapeAttr(doc.displayName)}" data-user-mail="${this.host.escapeAttr(doc.mail)}"${queued ? '' : ` role="button" tabindex="0" title="${hint}"`}>
                            <eum-person-avatar class="result-avatar"
                                display-name="${this.host.escapeAttr(doc.displayName)}"
                                has-image="${doc.hasImage ? 'true' : 'false'}"
                                ${doc.imageUrl ? `image-url="${this.host.escapeAttr(doc.imageUrl)}"` : ''}></eum-person-avatar>
                            <div class="result-main">
                                <div class="result-name">${this.host.escapeHTML(doc.displayName)}</div>
                                <div class="result-sub">${this.host.escapeHTML(doc.mail)}</div>
                                ${doc.companyName ? `<div class="result-sub">${this.host.escapeHTML(doc.companyName)}</div>` : ''}
                            </div>
                            <span class="result-status">
                                <span class="badge rounded-pill text-bg-success result-queued-badge" ${queued ? '' : 'hidden'}>${icon('check-lg')}<span>${this.host.escapeHTML(i18n.t('userToolbar.search.queued'))}</span></span>
                            </span>
                        </div>`;
                })
                .join('');

            resultsEl.querySelectorAll<HTMLElement>('.result-row[role="button"]').forEach((row) => {
                const activate = () => this.addExistingUser(row.dataset.userId ?? '', row.dataset.displayName ?? '', row.dataset.userMail ?? '', row);
                row.addEventListener('click', activate);
                row.addEventListener('keydown', (ev: KeyboardEvent) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        activate();
                    }
                });
            });
        } catch {
            resultsEl.innerHTML = `<div class="p-2 text-danger small">${this.host.escapeHTML(i18n.t('userToolbar.search.failed'))}</div>`;
        }
    }

    private addExistingUser(userId: string, displayName: string, mail: string, row: HTMLElement): void {
        if (!userId || !displayName || !mail) return;
        if (this.existingQueue.has(userId)) return;

        this.existingQueue.set(userId, { id: userId, displayName, mail });

        const name = this.splitDisplayName(displayName);
        const values: Record<string, string> = {
            [this.primaryEmailKey()]: mail,
        };
        const firstKey = this.firstNameKey();
        const lastKey = this.lastNameKey();
        const displayKey = this.displayNameKey();
        if (firstKey) values[firstKey] = name.first;
        if (lastKey) values[lastKey] = name.last;
        if (displayKey) values[displayKey] = displayName;

        const placeholderRow = this.getReviewRows().find((row) => this.isManualEmptyRow(row));
        if (placeholderRow) {
            this.populateReviewRow(placeholderRow, { values, existingUserId: userId });
            void this.hydrateExistingRow(placeholderRow, userId);
        } else {
            const row = this.addNewUserRow({ values, existingUserId: userId });
            if (row) void this.hydrateExistingRow(row, userId);
        }
        this.ensureTrailingEmptyRow();

        row.classList.add('is-queued');
        row.removeAttribute('role');
        row.removeAttribute('tabindex');
        row.removeAttribute('title');
        row.querySelector('.result-queued-badge')?.removeAttribute('hidden');

        this.updateQueueStatus();
    }

    private async submitAdd(): Promise<void> {
        const modal = this.addModalEl;
        if (!modal) return;

        if (this.addMode === 'existing') {
            if (!this.reviewQueueCount()) {
                this.setFooterStatus(i18n.t('userToolbar.toast.noUsersToAdd'), 'danger');
                return;
            }
            this.switchAddMode('new');
            return;
        }

        // On the paste tabs the primary button is purely navigational: it jumps
        // to the Review & Add Users tab. Queuing the textarea contents is the
        // job of the secondary "Add to queue" button. If nothing has been
        // queued yet, keep the user on the tab and nudge them.
        if (this.addMode === 'outlook' || this.addMode === 'excel') {
            if (!this.reviewQueueCount()) {
                this.setFooterStatus(i18n.t('userToolbar.toast.noUsersToAdd'), 'danger');
                return;
            }
            this.switchAddMode('new');
            return;
        }

        const users = this.collectNewUsers();
        if (!users.length) {
            this.setFooterStatus(i18n.t('userToolbar.toast.noUsersToAdd'), 'danger');
            return;
        }

        // Block submit while any review row has a validation error, focusing
        // the first offending field so the user can fix it.
        if (this.addMode === 'new') {
            const invalidRows = this.getReviewRows().filter((row) => !this.validateRow(row));
            if (invalidRows.length) {
                this.setFooterStatus(i18n.t('userToolbar.addModal.validation.required'), 'danger');
                invalidRows[0].querySelector<HTMLInputElement>('input.is-invalid')?.focus();
                return;
            }
        }

        const submitBtn = modal.querySelector('.add-submit') as HTMLButtonElement | null;
        if (submitBtn) submitBtn.disabled = true;
        // Lock the close controls (Cancel / X) while the add is in flight so the
        // sequential submit can't be interrupted mid-run.
        this.setCloseControlsDisabled(true);

        // Map each candidate back to its table row (when in `new` mode) so the
        // Status column can reflect live progress transitions.
        const rowFor = (user: AddCandidate): HTMLTableRowElement | null => {
            if (this.addMode !== 'new') return null;
            const rows = this.getReviewRows();
            if (user.existingUserId) {
                return rows.find((r) => r.dataset.existingUserId === user.existingUserId) ?? null;
            }
            const emailKey = this.primaryEmailKey();
            const wanted = (user.values[emailKey] ?? '').toLowerCase();
            return (
                rows.find(
                    (r) =>
                        !r.dataset.existingUserId &&
                        (this.fieldInput(r, emailKey)?.value.trim().toLowerCase() ?? '') === wanted
                ) ?? null
            );
        };

        let added = 0;
        let failed = 0;
        const total = users.length;
        let index = 0;
        for (const user of users) {
            index++;
            this.setFooterStatus(i18n.t('userToolbar.addModal.processing', { current: index, total }), 'primary');
            const row = rowFor(user);
            if (row) this.setRowProgress(row, user.existingUserId ? 'adding' : 'inviting');
            try {
                if (user.existingUserId) {
                    if (!this.host.groupId) continue;
                    await this.host.userService.addToGroup(user.existingUserId, this.host.groupId);
                } else {
                    const payload: Record<string, unknown> = {};
                    for (const [key, value] of Object.entries(user.values)) {
                        if (value) payload[key] = value;
                    }
                    payload.InitialGroups = this.host.groupId ? JSON.stringify([this.host.groupId]) : undefined;
                    await this.host.userService.inviteUser(payload);
                }
                added++;
                if (row) this.setRowProgress(row, user.existingUserId ? 'added' : 'invited');
            } catch {
                // Continue attempting the remaining users; report at the end.
                failed++;
                if (row) this.setRowProgress(row, 'error');
            }
        }

        if (submitBtn) submitBtn.disabled = false;
        this.setCloseControlsDisabled(false);

        // Any failure keeps the modal open so the user can review and retry the
        // highlighted rows. The outcome is reported in the footer status zone
        // (a toast would render on top of the still-open modal). The modal only
        // closes — with a success toast summarising the count — when every add
        // succeeded.
        if (failed > 0) {
            if (added > 0) {
                this.setFooterStatus(
                    i18n.t('userToolbar.toast.usersAddedPartial', { added, total, failed }),
                    'danger'
                );
                this.host.refreshResults();
            } else {
                this.setFooterStatus(i18n.t('userToolbar.toast.addUsersFailed'), 'danger');
            }
            return;
        }

        if (added > 0) {
            this.host.modals.hide(modal);
            this.host.toast.success(i18n.t('userToolbar.toast.usersAdded', { count: added }));
            this.host.refreshResults();
        } else {
            this.setFooterStatus(i18n.t('userToolbar.toast.addUsersFailed'), 'danger');
        }
    }

    /**
     * Queue users parsed from a paste pane (Outlook/Excel) into the Review table
     * and check each one's membership sequentially, then switch to the Review &
     * Add Users tab. Each row is populated from the existing user record when the
     * address is known (regardless of group), locked when already a member, or
     * left blank/editable (except email and User Status) for net-new invites —
     * matching the "Find Existing" queue-and-review behaviour.
     */
    private async queuePastedUsers(mode: 'outlook' | 'excel', opts: { stayOnTab?: boolean } = {}): Promise<void> {
        const modal = this.addModalEl;
        if (!modal) return;

        const selector = mode === 'outlook' ? '.paste-outlook' : '.paste-excel';
        const raw = (modal.querySelector(selector) as HTMLTextAreaElement | null)?.value ?? '';
        const candidates = mode === 'outlook' ? this.parseOutlook(raw) : this.parseExcel(raw);

        if (!candidates.length) {
            this.setFooterStatus(i18n.t('userToolbar.toast.noUsersToAdd'), 'danger');
            return;
        }

        const submitBtn = modal.querySelector('.add-submit') as HTMLButtonElement | null;
        const queueBtn = modal.querySelector('.add-to-queue') as HTMLButtonElement | null;
        this.queueingPaste = true;
        if (submitBtn) submitBtn.disabled = true;
        if (queueBtn) queueBtn.disabled = true;

        // Signal progress up front so it's evident the queue action is running.
        this.setFooterStatus(
            i18n.t('userToolbar.addModal.addingToQueue', { count: candidates.length }),
            'primary'
        );

        const emailKey = this.primaryEmailKey();
        const rows: HTMLTableRowElement[] = [];
        for (const candidate of candidates) {
            const email = (candidate.values[emailKey] ?? '').trim().toLowerCase();
            // Skip duplicates already represented in the Review table.
            const already = this.getReviewRows().some(
                (r) => (this.fieldInput(r, emailKey)?.value.trim().toLowerCase() ?? '') === email && !this.isManualEmptyRow(r)
            );
            if (email && already) continue;

            const placeholderRow = this.getReviewRows().find((r) => this.isManualEmptyRow(r));
            const row = placeholderRow ?? this.addNewUserRow();
            if (!row) continue;
            this.populateReviewRow(row, { values: candidate.values });
            this.ensureTrailingEmptyRow();
            rows.push(row);
        }

        // Check membership one row at a time so status transitions are visible.
        for (const row of rows) {
            await this.checkManualRowMembership(row);
        }

        // Clear the paste input now that its contents have been queued.
        const textarea = modal.querySelector(selector) as HTMLTextAreaElement | null;
        if (textarea) textarea.value = '';

        this.queueingPaste = false;
        if (submitBtn) submitBtn.disabled = false;

        if (opts.stayOnTab) {
            // Add-to-queue: stay on the paste tab and report how many users were
            // queued. updateFooterButtons re-evaluates the "Add to queue" button
            // against the now-empty textarea, leaving it disabled (count 0) —
            // i.e. it stays disabled from click through to completion.
            this.updateFooterButtons();
            this.setFooterStatus(
                i18n.t('userToolbar.addModal.addedToQueue', { count: rows.length }),
                'success'
            );
            return;
        }

        this.switchAddMode('new');
    }

    /** Collect users from whichever add pane is currently active. */
    private collectNewUsers(): AddCandidate[] {
        const modal = this.addModalEl;
        if (!modal) return [];

        if (this.addMode === 'new') {
            const rows = Array.from(modal.querySelectorAll('.new-users-table tbody tr'));
            return rows
                .filter((row) => !(row instanceof HTMLElement && row.dataset.rowStatus === 'already'))
                .map((row) => ({
                    values: this.rowValues(row as HTMLTableRowElement),
                    existingUserId: row instanceof HTMLElement ? (row.dataset.existingUserId ?? undefined) : undefined,
                }))
                .filter((u) => {
                    if (u.existingUserId) return true;
                    const email = u.values[this.primaryEmailKey()] ?? '';
                    return !!email;
                });
        }

        if (this.addMode === 'outlook') {
            const raw = (modal.querySelector('.paste-outlook') as HTMLTextAreaElement | null)?.value ?? '';
            return this.parseOutlook(raw);
        }

        if (this.addMode === 'excel') {
            const raw = (modal.querySelector('.paste-excel') as HTMLTextAreaElement | null)?.value ?? '';
            return this.parseExcel(raw);
        }

        return [];
    }

    /** Parse an Outlook paste: entries separated by ; or newlines, each "Name <email>" or bare email. */
    private parseOutlook(raw: string): AddCandidate[] {
        const firstKey = this.firstNameKey();
        const lastKey = this.lastNameKey();
        const displayKey = this.displayNameKey();
        const emailKey = this.primaryEmailKey();

        return raw
            .split(/[;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map((entry) => {
                const values: Record<string, string> = {};
                const match = entry.match(/^(.*?)<([^>]+)>$/);
                if (match) {
                    const name = match[1].trim().replace(/^"|"$/g, '');
                    const [first, ...rest] = name.split(/\s+/);
                    if (firstKey) values[firstKey] = first ?? '';
                    if (lastKey) values[lastKey] = rest.join(' ');
                    if (displayKey) values[displayKey] = name;
                    values[emailKey] = match[2].trim();
                } else {
                    values[emailKey] = entry;
                }
                return { values };
            })
            .filter((u) => /@/.test(String(u.values[emailKey] ?? '')));
    }

    /** Parse an Excel paste: tab-separated First, Last, Email per line. */
    private parseExcel(raw: string): AddCandidate[] {
        const firstKey = this.firstNameKey();
        const lastKey = this.lastNameKey();
        const displayKey = this.displayNameKey();
        const emailKey = this.primaryEmailKey();

        return raw
            .split(/\n/)
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
                const cols = line.split(/\t/).map((c) => c.trim());
                const first = cols[0] ?? '';
                const last = cols.length >= 2 ? cols[1] : '';
                const email = cols.length >= 3 ? cols[2] : cols[cols.length - 1] ?? '';
                const values: Record<string, string> = { [emailKey]: email };
                if (firstKey) values[firstKey] = first;
                if (lastKey) values[lastKey] = last;
                if (displayKey) values[displayKey] = [first, last].filter(Boolean).join(' ').trim();
                return { values };
            })
            .filter((u) => /@/.test(String(u.values[emailKey] ?? '')));
    }
}
